import {Trans} from '@lingui/macro';
import React, {ReactNode} from 'react';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  isLikePrivateAddress,
  parseDomainName,
} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {validateResolvedExpressionRefs} from '../../editors/expressions/expressionValidationHelpers';
import {PreviewerEditorState} from '../../editors/previewerEditor/previewerEditorState';

/*
 * Types.
 */

export enum PreviewerEditorFormErrorsEnum {
  INVALID_DOMAIN = 'invalid_domain',
  MISSING_PREVIEWER_NAME = 'missing_previewer_name',
  INVALID_EXPRESSIONS = 'invalid_expressions',
  MISSING_EXPRESSION = 'missing_expression',
}

type PreviewerChecker = (state: PreviewerEditorState) => PreviewerEditorFormErrorsEnum | undefined;

/*
 * Checkers.
 */

const ERROR_CHECKS: ReadonlyArray<PreviewerChecker> = [
  // Domain.
  ({previewerState}) => {
    const {domain} = previewerState;

    if (!domain || !parseDomainName(domain) || isLikePrivateAddress(domain)) {
      return PreviewerEditorFormErrorsEnum.INVALID_DOMAIN;
    }

    return undefined;
  },

  // Preview builder.
  ({previewerState, expressions}) => {
    const {previewBuilder} = previewerState;

    if (!previewBuilder.name) {
      return PreviewerEditorFormErrorsEnum.MISSING_PREVIEWER_NAME;
    }

    const hasValidReferences = validateResolvedExpressionRefs(previewBuilder, expressions);
    if (!hasValidReferences) {
      return PreviewerEditorFormErrorsEnum.MISSING_EXPRESSION;
    }

    return undefined;
  },

  // Expressions.
  ({expressions}) => {
    const areAllAtomsValid = expressions.every((expression) =>
      expression.atoms.every((atom) => atom.isValid),
    );

    return areAllAtomsValid ? undefined : PreviewerEditorFormErrorsEnum.INVALID_EXPRESSIONS;
  },
];

/*
 * Helpers.
 */

export function computePreviewerEditorFormErrors(
  state: PreviewerEditorState,
): ReadonlyArray<PreviewerEditorFormErrorsEnum> {
  return ERROR_CHECKS.map((checker) => checker(state)).filter(isDefined);
}

/*
 * Labels.
 */

export const previewerEditorFormErrorLabels: Record<PreviewerEditorFormErrorsEnum, ReactNode> = {
  [PreviewerEditorFormErrorsEnum.INVALID_DOMAIN]: (
    <Trans id="previewer-editor-footer-tooltip-warning-invalid-domain">
      Your previewer needs to target a valid domain name
    </Trans>
  ),

  [PreviewerEditorFormErrorsEnum.MISSING_PREVIEWER_NAME]: (
    <Trans id="previewer-editor-footer-tooltip-warning-missing-previewer-name">
      Need to set a name for previewed links.
    </Trans>
  ),

  [PreviewerEditorFormErrorsEnum.INVALID_EXPRESSIONS]: (
    <Trans id="previewer-editor-footer-tooltip-warning-invalid-expressions">
      Some of your dynamic variables are invalid
    </Trans>
  ),

  [PreviewerEditorFormErrorsEnum.MISSING_EXPRESSION]: (
    <Trans id="previewer-editor-footer-tooltip-warning-missing-expressions">
      Some of your dynamic variables are missing
    </Trans>
  ),
};
